import { useMemo, useState } from "react";
import { Lock, Plus, ShieldCheck, Trash2, Users } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Badge, Button, Field, Input } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";
import type { Permission, Role } from "./api";
import {
  useCreateRole,
  useDeleteRole,
  usePermissionCatalogue,
  useRolesAdmin,
  useUpdateRole,
} from "./hooks";

const GROUP_LABELS: Record<string, string> = {
  user: "Users & access",
  role: "Users & access",
  customer: "Customers",
  ticket: "Tickets",
  sla: "SLA",
  asset: "Assets",
  inventory: "Inventory",
  report: "Reports",
  audit: "System",
  settings: "System",
};

/** Group the flat catalogue by its display section, preserving catalogue order. */
function groupPermissions(perms: Permission[]): [string, Permission[]][] {
  const out = new Map<string, Permission[]>();
  for (const p of perms) {
    const label = GROUP_LABELS[p.group] ?? p.group;
    (out.get(label) ?? out.set(label, []).get(label)!).push(p);
  }
  return [...out.entries()];
}

export default function RolesPage() {
  const { data: roles, isLoading } = useRolesAdmin();
  const { data: permissions } = usePermissionCatalogue();
  // null = nothing selected, "new" = create form, otherwise a role id.
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selected = selectedId && selectedId !== "new"
    ? roles?.find((r) => r.id === selectedId) ?? null
    : null;
  const showEditor = selectedId === "new" || selected != null;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Roles &amp; permissions</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Control what each role can do. System roles are locked; create custom
            roles for anything else.
          </p>
        </div>
        <Button onClick={() => setSelectedId("new")}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          New role
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(220px,280px)_1fr]">
        <aside className="space-y-1.5">
          {isLoading && <p className="text-sm text-muted-foreground">Loading roles…</p>}
          {roles?.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setSelectedId(r.id)}
              aria-current={selectedId === r.id ? "true" : undefined}
              className={cn(
                "flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors",
                selectedId === r.id
                  ? "border-primary/50 bg-accent"
                  : "border-border hover:bg-accent",
              )}
            >
              <ShieldCheck className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{r.name}</span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Users className="h-3 w-3" aria-hidden="true" />
                  {r.user_count}
                </span>
              </span>
              {r.is_system && <Badge tone="muted">System</Badge>}
            </button>
          ))}
        </aside>

        <section>
          {!showEditor && (
            <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
              Select a role to view its permissions, or create a new one.
            </div>
          )}
          {showEditor && permissions && (
            <RoleEditor
              key={selectedId}
              role={selected}
              permissions={permissions}
              onSaved={(id) => setSelectedId(id)}
              onDeleted={() => setSelectedId(null)}
            />
          )}
        </section>
      </div>
    </div>
  );
}

function RoleEditor({
  role,
  permissions,
  onSaved,
  onDeleted,
}: {
  role: Role | null;
  permissions: Permission[];
  onSaved: (id: string) => void;
  onDeleted: () => void;
}) {
  const isNew = role == null;
  const isSystem = role?.is_system ?? false;
  const isAdminRole = role?.code === "admin";
  // The Administrator role is fully read-only (the superuser safety net).
  // Other built-in roles keep a fixed name/description but allow their
  // permissions to be edited. Custom roles are fully editable.
  const permsLocked = isAdminRole;
  const identityLocked = isSystem;

  const [name, setName] = useState(role?.name ?? "");
  const [description, setDescription] = useState(role?.description ?? "");
  const [granted, setGranted] = useState<Set<string>>(new Set(role?.permissions ?? []));
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const create = useCreateRole();
  const update = useUpdateRole();
  const del = useDeleteRole();
  const busy = create.isPending || update.isPending || del.isPending;

  const groups = useMemo(() => groupPermissions(permissions), [permissions]);

  function toggle(code: string) {
    setGranted((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  async function save() {
    setError(null);
    const body = {
      name: name.trim(),
      description: description.trim() || null,
      permissions: [...granted],
    };
    try {
      if (isNew) {
        const created = await create.mutateAsync(body);
        onSaved(created.id);
      } else {
        const saved = await update.mutateAsync({ id: role.id, body });
        onSaved(saved.id);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the role.");
    }
  }

  async function remove() {
    if (!role) return;
    setError(null);
    try {
      await del.mutateAsync(role.id);
      onDeleted();
    } catch (err) {
      setConfirmDelete(false);
      setError(err instanceof ApiError ? err.message : "Could not delete the role.");
    }
  }

  return (
    <div className="space-y-5 rounded-lg border border-border bg-card p-6 text-card-foreground">
      <div className="flex items-start justify-between gap-4">
        <h2 className="text-lg font-semibold">
          {isNew ? "New role" : role.name}
        </h2>
        {isAdminRole ? (
          <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <Lock className="h-4 w-4" aria-hidden="true" />
            Locked (system role)
          </span>
        ) : isSystem ? (
          <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <Lock className="h-4 w-4" aria-hidden="true" />
            Built-in role — name is fixed
          </span>
        ) : null}
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {error}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name" htmlFor="role_name">
          <Input
            id="role_name"
            value={name}
            disabled={identityLocked}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Dispatch Coordinator"
          />
        </Field>
        <Field label="Description" htmlFor="role_desc">
          <Input
            id="role_desc"
            value={description}
            disabled={identityLocked}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this role is for"
          />
        </Field>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-medium">Permissions</h3>
          <span className="text-xs text-muted-foreground">{granted.size} selected</span>
        </div>
        <div className="space-y-4">
          {groups.map(([label, perms]) => (
            <fieldset key={label} className="rounded-md border border-border p-3">
              <legend className="px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {label}
              </legend>
              <div className="grid gap-x-4 gap-y-1.5 sm:grid-cols-2">
                {perms.map((p) => (
                  <label
                    key={p.code}
                    className={cn(
                      "flex items-start gap-2 text-sm",
                      permsLocked ? "cursor-default" : "cursor-pointer",
                    )}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={granted.has(p.code)}
                      disabled={permsLocked}
                      onChange={() => toggle(p.code)}
                    />
                    <span>
                      <span className="block">{p.description}</span>
                      <span className="block font-mono text-xs text-muted-foreground">
                        {p.code}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          ))}
        </div>
      </div>

      {!permsLocked && (
        <div className="flex items-center justify-between gap-2 border-t border-border pt-4">
          <div>
            {!isNew && !isSystem && (
              confirmDelete ? (
                <span className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Delete this role?</span>
                  <Button variant="destructive" disabled={busy} onClick={() => void remove()}>
                    Yes, delete
                  </Button>
                  <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
                    Cancel
                  </Button>
                </span>
              ) : (
                <Button
                  variant="outline"
                  disabled={busy || (role?.user_count ?? 0) > 0}
                  title={
                    (role?.user_count ?? 0) > 0
                      ? "Reassign its users before deleting"
                      : undefined
                  }
                  onClick={() => setConfirmDelete(true)}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                  Delete
                </Button>
              )
            )}
          </div>
          <Button disabled={busy || name.trim().length < 2} onClick={() => void save()}>
            {isNew ? "Create role" : "Save changes"}
          </Button>
        </div>
      )}
    </div>
  );
}
